import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {} from '@redux-devtools/extension'
import { apiRequest } from '@/lib/api'
import { pendingAiSuggestionCount } from '@/lib/gng-score'

// ===================
// Types
// ===================

export type GngCategory = string
export type GngDecision = 'go' | 'conditional_go' | 'no_go'
export type ThemeCategory = 'experience' | 'personnel' | 'methodology' | 'innovation' | 'relationship' | 'value' | 'risk' | 'local_presence' | 'other'
export type ThemeStrength = 'strong' | 'moderate' | 'weak'

export type AiScoringStatus = 'scoring' | 'completed' | 'failed' | null

export interface AiScoringSource {
  documentId: string
  documentName: string
  pageNumber: number | null
}

export interface GoNoGoAssessment {
  id: string
  organizationId: string
  bidId: string
  status: 'in_progress' | 'completed'
  overallScore: number | null
  recommendation: GngDecision | null
  decision: GngDecision | null
  decisionNotes: string | null
  decisionConditions: string | null
  decidedBy: string | null
  decidedAt: string | null
  thresholds: { go: number; conditionalGo: number; noGo: number }
  aiScoringStatus: AiScoringStatus
  /** Coarse progress of an in-flight batch scoring run. Loosely typed so an
      unknown future stage renders a generic label instead of breaking. */
  aiScoringStage: string | null
  aiScoringStartedAt: string | null
  aiScoringSources: AiScoringSource[] | null
  createdBy: string
  createdAt: string
  updatedAt: string
  criteria: GoNoGoCriterion[]
}

export interface GoNoGoCriterion {
  id: string
  assessmentId: string
  templateCriterionId: string | null
  category: GngCategory
  title: string
  description: string | null
  weight: number
  isMandatory: boolean
  score: number | null
  evidence: string | null
  aiSuggestedScore: number | null
  aiRationale: string | null
  aiConfidence: 'high' | 'medium' | 'low' | null
  aiScoringStatus: AiScoringStatus
  source: 'template' | 'ai' | 'manual'
  sortOrder: number
  createdAt: string
  updatedAt: string
}

export interface ThemeEvidence {
  type: 'project' | 'personnel' | 'statistic' | 'library' | 'freetext'
  detail: string
  metadata?: Record<string, unknown>
}

export interface ThemeSource {
  documentId: string
  documentName: string
  pageNumber: number | null
}

export type ThemeRefineStatus = 'refining' | 'completed' | 'failed' | null

export interface RefinedTheme {
  title: string
  description: string
  category: string
  strength: string
  evidence: { type: string; detail: string }[]
  sources: ThemeSource[]
  instruction: string
}

export type EvidenceStrength = 'strong' | 'partial' | 'gap'

export interface WinTheme {
  id: string
  organizationId: string
  bidId: string
  bankThemeId: string | null
  title: string
  description: string | null
  category: ThemeCategory
  strength: ThemeStrength
  evidence: ThemeEvidence[]
  sources: ThemeSource[] | null
  source: 'manual' | 'ai' | 'bank'
  sortOrder: number
  evaluationCriteriaIds: string[]
  createdBy: string
  createdAt: string
  updatedAt: string
  refineStatus: ThemeRefineStatus
  refineResult: RefinedTheme | null
  evidenceStrength: EvidenceStrength | null
  libraryEvidenceCount: number
}

/** A theme proposed by AI but not yet accepted. Lives in the JSONB pending blob. */
export interface PendingThemeSuggestion {
  id: string
  title: string
  category: string
  strength: 'strong' | 'moderate' | 'weak'
  evidenceStrength: EvidenceStrength
  libraryEvidenceCount: number
  description: string
  evidence: ThemeEvidence[]
  mappedCriteriaIds: string[]
  rationale: string
  sources: ThemeSource[]
}

/** A bank theme that matches the current tender — surfaced in the "Also consider" tier. */
export interface BankSuggestionMatch {
  bankThemeId: string
  title: string
  description: string | null
  category: ThemeCategory
  timesUsed: number
  timesWon: number
  matchedCriteriaIds: string[]
  matchReason: string
}

/** v2 schema returned by GET /strategy/themes/suggest. */
interface PendingThemeSuggestionsBlob {
  schemaVersion: 2
  generatedAt: string
  aiSuggestions: PendingThemeSuggestion[]
  bankMatches: BankSuggestionMatch[]
  coverageAnalysis: {
    addressedCriteria: string[]
    unaddressedCriteria: string[]
    suggestion: string | null
  }
}

export interface CoverageMatrix {
  rows: {
    criterionId: string
    criterionTitle: string
    weightPercentage: number | null
    weightPoints: number | null
    themeIds: string[]
  }[]
  coveragePercent: number
  totalCriteria: number
  coveredCriteria: number
}

// ===================
// Store Interface
// ===================

export type ThemeSuggestionStatus = 'suggesting' | 'completed' | 'failed' | null

// ===================
// Bid Brief Types
// ===================

export type BidBriefSource = 'manual' | 'ai' | 'mixed'
export type BidBriefStatus = 'draft' | 'approved'
export type BriefGenerationStatus = 'pending' | 'completed' | 'failed'

export interface BidBriefStructured {
  strategicNarrative: string | null
  clientPriorities: string | null
  risksAndWatchouts: string | null
  voiceAndPosture: string | null
  evaluatorLens: string | null
}

export interface BriefCitation {
  marker: string
  documentId: string
  documentTitle: string
  chunkIndex?: number
  pageNumber?: number | null
}

export interface BidBrief {
  id: string
  organizationId: string
  bidId: string
  structured: BidBriefStructured
  freeformNotes: string | null
  source: BidBriefSource
  status: BidBriefStatus
  generationMetadata: {
    generatedAt: string
    modelId: string
    citations: BriefCitation[]
    freeformNotesPresentAtGeneration: boolean
  } | null
  createdBy: string
  updatedBy: string
  createdAt: string
  updatedAt: string
}

export interface BriefGenerationStatusResponse {
  generationId: string
  status: BriefGenerationStatus
  result: {
    structured: BidBriefStructured
    citations: BriefCitation[]
    modelId: string
  } | null
  errorMessage: string | null
  startedAt: string
  completedAt: string | null
}

interface StrategyState {
  // State
  currentBidId: string | null
  assessment: GoNoGoAssessment | null
  themes: WinTheme[]
  coverageMatrix: CoverageMatrix | null

  // Pending AI suggestions + bank matches (persisted across navigation in the JSONB blob)
  pendingSuggestions: PendingThemeSuggestion[]
  bankMatches: BankSuggestionMatch[]
  themeSuggestionStatus: ThemeSuggestionStatus

  // Loading flags
  isLoadingAssessment: boolean
  isLoadingThemes: boolean
  isCreatingAssessment: boolean
  isRecordingDecision: boolean
  isSavingCriterion: boolean
  isSavingTheme: boolean
  isAiScoring: boolean
  isApplyingAiScores: boolean
  isSuggestingThemes: boolean

  // Error
  error: string | null
  /**
   * Why the last Go/No-Go scoring run stopped, when it stopped badly.
   *
   * `failed` — the run is over and nothing was saved; offer a re-run.
   * `still_running` — we stopped waiting but the server may yet finish; offer a
   * refetch only. Re-running would discard a result that is about to land.
   */
  aiScoringError: { kind: 'failed' | 'still_running'; message: string } | null

  // Go/No-Go actions
  loadAssessment: (bidId: string) => Promise<void>
  createAssessment: (bidId: string) => Promise<void>
  updateCriterion: (bidId: string, criterionId: string, data: {
    score?: number | null
    evidence?: string | null
    weight?: number
    category?: GngCategory
    sortOrder?: number
  }) => Promise<void>
  addCriterion: (bidId: string, data: {
    category: GngCategory
    title: string
    description?: string | null
    weight?: number
    isMandatory?: boolean
  }) => Promise<void>
  removeCriterion: (bidId: string, criterionId: string) => Promise<void>
  recordDecision: (bidId: string, data: {
    decision: GngDecision
    decisionNotes?: string | null
    decisionConditions?: string | null
    transitionBidStatus?: boolean
  }) => Promise<void>
  reopenAssessment: (bidId: string) => Promise<void>
  triggerAiScoring: (bidId: string) => Promise<void>
  triggerAiScoringForCriterion: (bidId: string, criterionId: string) => Promise<void>
  /** Refetch the assessment without re-running scoring. */
  refreshAssessment: (bidId: string) => Promise<void>
  /** Resolves false when the bulk apply failed — the caller is expected to tell the user. */
  acceptAllAiScores: (bidId: string) => Promise<boolean>
  resumeAiScoringIfNeeded: (bidId: string) => Promise<void>

  // Win Themes actions
  loadThemes: (bidId: string) => Promise<void>
  createTheme: (bidId: string, data: {
    title: string
    description?: string | null
    category: ThemeCategory
    strength?: ThemeStrength
    evidence?: ThemeEvidence[]
    evaluationCriteriaIds?: string[]
    sources?: ThemeSource[] | null
    source?: 'manual' | 'ai' | 'bank'
    evidenceStrength?: EvidenceStrength | null
    libraryEvidenceCount?: number
  }) => Promise<void>
  updateTheme: (bidId: string, themeId: string, data: {
    title?: string
    description?: string | null
    category?: ThemeCategory
    strength?: ThemeStrength
    evidence?: ThemeEvidence[]
    evaluationCriteriaIds?: string[]
    sources?: ThemeSource[] | null
    sortOrder?: number
  }) => Promise<void>
  removeTheme: (bidId: string, themeId: string) => Promise<void>
  suggestThemes: (bidId: string) => Promise<void>
  loadThemeSuggestionStatus: (bidId: string) => Promise<void>
  /** Resolves false when the accept failed — the caller is expected to tell the user. */
  acceptSuggestion: (bidId: string, sid: string) => Promise<boolean>
  /** Resolves false when the accept failed — the caller is expected to tell the user. */
  acceptAllSuggestions: (bidId: string) => Promise<boolean>
  dismissSuggestion: (bidId: string, sid: string) => Promise<void>
  discardAllSuggestions: (bidId: string) => Promise<void>
  refinePendingSuggestion: (bidId: string, sid: string, instruction: string) => Promise<void>
  addBankMatchToBid: (bidId: string, bankThemeId: string) => Promise<void>
  addThemeWithAi: (bidId: string, userPrompt: string) => Promise<PendingThemeSuggestion[]>
  loadCoverageMatrix: (bidId: string) => Promise<void>
  refineTheme: (bidId: string, themeId: string, instruction: string) => Promise<void>
  acceptRefinement: (bidId: string, themeId: string) => Promise<void>
  dismissRefinement: (bidId: string, themeId: string) => Promise<void>

  // Bid Brief state
  bidBrief: BidBrief | null
  isLoadingBrief: boolean
  isSavingBrief: boolean
  isGeneratingBrief: boolean
  briefGenerationStatus: BriefGenerationStatus | 'idle'
  briefGenerationError: string | null
  isConvertingNotes: boolean
  convertNotesProposed: BidBriefStructured | null

  // Bid Brief actions
  loadBrief: (bidId: string) => Promise<void>
  loadBriefGenerationStatus: (bidId: string) => Promise<void>
  saveBrief: (bidId: string, patch: {
    structured?: Partial<BidBriefStructured>
    freeformNotes?: string | null
    source?: BidBriefSource
    status?: BidBriefStatus
  }) => Promise<void>
  deleteBrief: (bidId: string) => Promise<void>
  setBriefStatus: (bidId: string, status: BidBriefStatus) => Promise<void>
  generateBrief: (bidId: string) => Promise<void>
  pollBriefGeneration: (bidId: string) => Promise<void>
  convertNotesToFields: (bidId: string, freeformNotes: string) => Promise<void>
  applyConvertedNotes: (bidId: string, accepted: Partial<BidBriefStructured>) => Promise<void>
  clearConvertedNotes: () => void

  // Utility
  clearError: () => void
  reset: () => void
}

// ===================
// Store
// ===================

const initialState = {
  currentBidId: null as string | null,
  assessment: null as GoNoGoAssessment | null,
  themes: [] as WinTheme[],
  coverageMatrix: null as CoverageMatrix | null,
  pendingSuggestions: [] as PendingThemeSuggestion[],
  bankMatches: [] as BankSuggestionMatch[],
  themeSuggestionStatus: null as ThemeSuggestionStatus,
  isLoadingAssessment: false,
  isLoadingThemes: false,
  isCreatingAssessment: false,
  isRecordingDecision: false,
  isSavingCriterion: false,
  isSavingTheme: false,
  isAiScoring: false,
  isApplyingAiScores: false,
  isSuggestingThemes: false,
  error: null as string | null,
  /**
   * Why the last scoring run stopped, when it stopped badly. Deliberately not
   * the catch-all `error` above: that one is written by ~20 unrelated actions,
   * and the Go/No-Go panel must not surface a win-theme failure.
   */
  aiScoringError: null as { kind: 'failed' | 'still_running'; message: string } | null,
  bidBrief: null as BidBrief | null,
  isLoadingBrief: false,
  isSavingBrief: false,
  isGeneratingBrief: false,
  briefGenerationStatus: 'idle' as BriefGenerationStatus | 'idle',
  briefGenerationError: null as string | null,
  isConvertingNotes: false,
  convertNotesProposed: null as BidBriefStructured | null,
}

// Module-level polling guards — prevent duplicate poll loops when users
// switch tabs, trigger re-renders, or mount/unmount StrategySection.
// These are intentionally outside the store: they track in-flight network work,
// not UI state, and shouldn't be wiped by store.reset().
let themePollingInFlight = false
let scoringPollingInFlight = false
let briefPollingInFlight = false
const refineThemePollsInFlight = new Set<string>()

const POLL_INTERVAL_MS = 3000
const MAX_POLL_ATTEMPTS = 40

/**
 * Go/No-Go scoring polls far longer than anything else in this store. A batch
 * run is a Haiku query pass, a retrieval pass, then one or two ~90s Sonnet
 * calls, and `StrategyFunction` gives the whole thing 300s
 * (backend/template.yaml). Measured prod runs on 2026-08-17: 123s and 200s.
 *
 * This budget MUST stay above the Lambda's, or the UI declares a timeout on a
 * run the server is about to finish. That is exactly what shipped on
 * 2026-08-13: the Lambda went 120s -> 300s and this loop kept the shared 40
 * attempts (120s), so a 123s prod run completed 3 seconds after the browser had
 * given up and the user never saw their scores.
 *
 * 110 x 3s = 330s — the 300s ceiling plus cold start and the final write.
 * Deliberately NOT `MAX_POLL_ATTEMPTS`: the theme and brief loops finish in well
 * under a minute, and stretching them to 5.5 minutes would hang their spinners
 * on every dead worker.
 */
const SCORING_MAX_POLL_ATTEMPTS = 110

// A brief generation runs inside StrategyFunction and finishes in well under a
// minute. A `pending` row older than this is a worker that died without writing
// its failure — resuming its spinner would hang the tab forever.
const BRIEF_GENERATION_STALE_MS = 10 * 60 * 1000

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

/**
 * True once the store has moved to a *different* bid than `bidId`.
 *
 * Every entity write below lands after an `await`, and the user can move between
 * bids without the page being rebuilt — the notifications bell navigates to a
 * bare bid link, and so do browser Back/Forward. An unguarded `set()` then
 * publishes the previous bid's assessment or brief under the current bid's URL.
 *
 * This was measured, not theorised: a criterion save on bid A resolved *after*
 * bid B's page had finished loading, and its refetch of bid A's assessment
 * landed last.
 *
 * Deliberately false when `currentBidId` is null. Null means "the store is not
 * scoped to any bid yet", which is absence of information, not evidence of a
 * mismatch — treating it as stale silently discarded legitimate writes on every
 * path that reaches this store without loading an assessment first.
 */
function hasMovedToAnotherBid(bidId: string): boolean {
  const { currentBidId } = useStrategyStore.getState()
  return currentBidId !== null && currentBidId !== bidId
}

export const useStrategyStore = create<StrategyState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // ── Go/No-Go ──────────────────────────────

      loadAssessment: async (bidId: string) => {
        set({ isLoadingAssessment: true, error: null, currentBidId: bidId })
        try {
          const assessment = await apiRequest<GoNoGoAssessment | null>(
            `/bids/${bidId}/strategy/assessment`
          )
          if (hasMovedToAnotherBid(bidId)) return
          set({ assessment, isLoadingAssessment: false })
        } catch (err) {
          set({ isLoadingAssessment: false, error: (err as Error).message })
        }
      },

      createAssessment: async (bidId: string) => {
        set({ isCreatingAssessment: true, error: null })
        try {
          const assessment = await apiRequest<GoNoGoAssessment>(
            `/bids/${bidId}/strategy/assessment`,
            { method: 'POST' }
          )
          if (hasMovedToAnotherBid(bidId)) return
          set({ assessment, isCreatingAssessment: false })
        } catch (err) {
          set({ isCreatingAssessment: false, error: (err as Error).message })
        }
      },

      updateCriterion: async (bidId: string, criterionId: string, data) => {
        set({ isSavingCriterion: true, error: null })
        try {
          await apiRequest<GoNoGoCriterion>(
            `/bids/${bidId}/strategy/criteria/${criterionId}`,
            { method: 'PATCH', body: data }
          )

          // Re-fetch to get updated overall score
          const { assessment } = get()
          if (assessment) {
            // Re-fetch to get updated overall score
            const refreshed = await apiRequest<GoNoGoAssessment | null>(
              `/bids/${bidId}/strategy/assessment`
            )
            if (hasMovedToAnotherBid(bidId)) return
            set({ assessment: refreshed, isSavingCriterion: false })
          } else {
            set({ isSavingCriterion: false })
          }
        } catch (err) {
          set({ isSavingCriterion: false, error: (err as Error).message })
        }
      },

      addCriterion: async (bidId: string, data) => {
        set({ isSavingCriterion: true, error: null })
        try {
          await apiRequest<GoNoGoCriterion>(
            `/bids/${bidId}/strategy/criteria`,
            { method: 'POST', body: data }
          )
          // Reload full assessment to get updated criteria list
          await get().loadAssessment(bidId)
          set({ isSavingCriterion: false })
        } catch (err) {
          set({ isSavingCriterion: false, error: (err as Error).message })
        }
      },

      removeCriterion: async (bidId: string, criterionId: string) => {
        set({ error: null })
        try {
          await apiRequest(`/bids/${bidId}/strategy/criteria/${criterionId}`, { method: 'DELETE' })
          // Reload full assessment
          await get().loadAssessment(bidId)
        } catch (err) {
          set({ error: (err as Error).message })
        }
      },

      recordDecision: async (bidId: string, data) => {
        set({ isRecordingDecision: true, error: null })
        try {
          const updated = await apiRequest<GoNoGoAssessment>(
            `/bids/${bidId}/strategy/decision`,
            { method: 'POST', body: data }
          )
          if (hasMovedToAnotherBid(bidId)) return
          set({ assessment: updated, isRecordingDecision: false })
        } catch (err) {
          set({ isRecordingDecision: false, error: (err as Error).message })
        }
      },

      reopenAssessment: async (bidId: string) => {
        set({ error: null })
        try {
          const updated = await apiRequest<GoNoGoAssessment>(
            `/bids/${bidId}/strategy/decision/reopen`,
            { method: 'POST' }
          )
          if (hasMovedToAnotherBid(bidId)) return
          set({ assessment: updated })
        } catch (err) {
          set({ error: (err as Error).message })
        }
      },

      triggerAiScoring: async (bidId: string) => {
        set({ isAiScoring: true, error: null, aiScoringError: null })
        try {
          // Fire async scoring — returns 202 immediately
          await apiRequest<GoNoGoAssessment>(
            `/bids/${bidId}/strategy/assessment/ai-score`,
            { method: 'POST' }
          )
          await pollAiScoring(bidId)
        } catch (err) {
          // The poll loop never started, so this failure has no other route to
          // the screen — without `aiScoringError` a 500 here is exactly as
          // invisible as the bug this whole change exists to fix.
          set({
            isAiScoring: false,
            error: (err as Error).message,
            aiScoringError: { kind: 'failed', message: (err as Error).message },
          })
        }
      },

      refreshAssessment: async (bidId: string) => {
        // Deliberately NOT a re-run: the `still_running` case means the server
        // may be about to finish, and re-scoring would discard that result and
        // cost another ~$0.20 and ~3 minutes.
        try {
          const refreshed = await apiRequest<GoNoGoAssessment | null>(
            `/bids/${bidId}/strategy/assessment`
          )
          if (hasMovedToAnotherBid(bidId)) return

          // Still running. Storing this row raw would be the worst of both
          // worlds: `isScoringRun` reads the 'scoring' status and re-arms the
          // progress readout, but no poll loop is alive to ever resolve it —
          // the exact stuck-spinner `clearedScoringStatus` exists to prevent.
          // Re-attach a poll instead, so the clock that reappears is real.
          if (refreshed?.aiScoringStatus === 'scoring') {
            set({ assessment: refreshed, aiScoringError: null, isAiScoring: true })
            void pollAiScoring(bidId)
            return
          }
          set({ assessment: refreshed, aiScoringError: null })
        } catch (err) {
          set({ error: (err as Error).message })
        }
      },

      triggerAiScoringForCriterion: async (bidId: string, criterionId: string) => {
        // Optimistically mark just this criterion as scoring so the per-card
        // spinner appears instantly. Server confirms via polling below.
        const { assessment } = get()
        set({ aiScoringError: null })
        if (assessment) {
          set({
            assessment: {
              ...assessment,
              criteria: assessment.criteria.map(c =>
                c.id === criterionId ? { ...c, aiScoringStatus: 'scoring' } : c
              ),
            },
          })
        }
        try {
          // Folded into PATCH /criteria/{cid} via _aiScore discriminator —
          // CloudFormation has a 500-resource ceiling on the SAM template.
          await apiRequest<{ criterionId: string; aiScoringStatus: AiScoringStatus }>(
            `/bids/${bidId}/strategy/criteria/${criterionId}`,
            { method: 'PATCH', body: { _aiScore: true } }
          )
          await pollCriterionAiScoring(bidId, criterionId)
        } catch (err) {
          // Roll back the optimistic status and surface the error. The per-card
          // spinner stopping is not by itself a message — the panel-level notice
          // is what actually tells the user the request never got started.
          const current = get().assessment
          const failure = { kind: 'failed' as const, message: (err as Error).message }
          if (current) {
            set({
              assessment: {
                ...current,
                criteria: current.criteria.map(c =>
                  c.id === criterionId ? { ...c, aiScoringStatus: 'failed' } : c
                ),
              },
              error: (err as Error).message,
              aiScoringError: failure,
            })
          } else {
            set({ error: (err as Error).message, aiScoringError: failure })
          }
        }
      },

      acceptAllAiScores: async (bidId: string) => {
        const { assessment } = get()
        if (!assessment) return true
        if (pendingAiSuggestionCount(assessment.criteria) === 0) return true

        set({ isApplyingAiScores: true, error: null })
        try {
          // One transactional call returns the full updated assessment, so there
          // is no second fetch and nothing to reconcile optimistically.
          const updated = await apiRequest<GoNoGoAssessment>(
            `/bids/${bidId}/strategy/assessment/ai-score`,
            { method: 'POST', body: { _action: 'accept-all' } }
          )
          if (hasMovedToAnotherBid(bidId)) return true
          set({ assessment: updated, isApplyingAiScores: false })
          return true
        } catch (err) {
          // Nothing was applied server-side, so there is no local state to roll
          // back — but a silent failure is indistinguishable from a dead button,
          // so the caller has to surface it.
          set({ isApplyingAiScores: false, error: (err as Error).message })
          return false
        }
      },

      resumeAiScoringIfNeeded: async (bidId: string) => {
        // Called on mount. If the assessment is currently being scored in the
        // background (status='scoring' in DB), restart the poll loop so the UI
        // sees the results when the worker finishes — even if the user navigated
        // away mid-scoring.
        const { assessment } = get()
        if (!assessment || assessment.aiScoringStatus !== 'scoring') return
        // Set the flag BEFORE the in-flight guard: reset() on unmount clears
        // isAiScoring but scoringPollingInFlight is module-scoped and survives,
        // so a remount mid-run would otherwise leave the flag false forever.
        set({ isAiScoring: true, error: null, aiScoringError: null })
        if (scoringPollingInFlight) return
        await pollAiScoring(bidId)
      },

      // ── Win Themes ────────────────────────────

      loadThemes: async (bidId: string) => {
        // Scoping the store here too, not just in loadAssessment: the Strategy tab
        // never loads an assessment, so without this `currentBidId` stayed null
        // and every staleness guard below silently no-opped for those users.
        set({ isLoadingThemes: true, error: null, currentBidId: bidId })
        try {
          const themes = await apiRequest<WinTheme[]>(`/bids/${bidId}/strategy/themes`)
          set({ themes, isLoadingThemes: false })
          // Resume any in-flight refines that were started before the page reload
          for (const theme of themes) {
            if (theme.refineStatus === 'refining') {
              void pollThemeRefinement(bidId, theme.id)
            }
          }
        } catch (err) {
          set({ isLoadingThemes: false, error: (err as Error).message })
        }
      },

      createTheme: async (bidId: string, data) => {
        set({ isSavingTheme: true, error: null })
        try {
          const theme = await apiRequest<WinTheme>(
            `/bids/${bidId}/strategy/themes`,
            { method: 'POST', body: data }
          )
          set(state => ({ themes: [...state.themes, theme], isSavingTheme: false }))
        } catch (err) {
          set({ isSavingTheme: false, error: (err as Error).message })
        }
      },

      updateTheme: async (bidId: string, themeId: string, data) => {
        set({ isSavingTheme: true, error: null })
        try {
          const updated = await apiRequest<WinTheme>(
            `/bids/${bidId}/strategy/themes/${themeId}`,
            { method: 'PATCH', body: data }
          )
          set(state => ({
            themes: state.themes.map(t => t.id === themeId ? updated : t),
            isSavingTheme: false,
          }))
        } catch (err) {
          set({ isSavingTheme: false, error: (err as Error).message })
        }
      },

      removeTheme: async (bidId: string, themeId: string) => {
        set({ error: null })
        try {
          await apiRequest(`/bids/${bidId}/strategy/themes/${themeId}`, { method: 'DELETE' })
          set(state => ({ themes: state.themes.filter(t => t.id !== themeId) }))
        } catch (err) {
          set({ error: (err as Error).message })
        }
      },

      suggestThemes: async (bidId: string) => {
        set({
          isSuggestingThemes: true,
          error: null,
          themeSuggestionStatus: 'suggesting',
          pendingSuggestions: [],
          bankMatches: [],
        })
        try {
          // Fire async suggestion — returns 202 immediately. Body is empty; the
          // _action discriminator defaults to 'generate'.
          await apiRequest(
            `/bids/${bidId}/strategy/themes/suggest`,
            { method: 'POST' }
          )
          await pollThemeSuggestions(bidId)
        } catch (err) {
          set({ isSuggestingThemes: false, themeSuggestionStatus: 'failed', error: (err as Error).message })
        }
      },

      loadThemeSuggestionStatus: async (bidId: string) => {
        // Hydrate pending suggestions + bank matches from the backend blob.
        // Four branches per spec §6.1:
        //   1. status='suggesting' → resume polling
        //   2. status='completed' with non-empty aiSuggestions OR bankMatches → populate state
        //   3. status='completed' with both empty → treat as null (hide inline cards)
        //   4. status='failed'/null → clear state
        try {
          const poll = await apiRequest<{
            status: ThemeSuggestionStatus
            suggestions: PendingThemeSuggestionsBlob | null
          }>(`/bids/${bidId}/strategy/themes/suggest`)

          if (poll.status === 'suggesting') {
            // Restore the processing state BEFORE the in-flight guard — a tab
            // switch resets the store while the detached poll loop keeps
            // running, and bailing first would leave the UI in empty state.
            set({ isSuggestingThemes: true, themeSuggestionStatus: 'suggesting' })
            if (themePollingInFlight) return
            await pollThemeSuggestions(bidId)
            return
          }

          if (poll.status === 'completed' && poll.suggestions) {
            const hasContent =
              (poll.suggestions.aiSuggestions?.length ?? 0) > 0 ||
              (poll.suggestions.bankMatches?.length ?? 0) > 0
            if (hasContent) {
              set({
                themeSuggestionStatus: 'completed',
                pendingSuggestions: poll.suggestions.aiSuggestions,
                bankMatches: poll.suggestions.bankMatches,
                isSuggestingThemes: false,
              })
              return
            }
            // Completed but empty — fall through to the null branch below.
          }

          if (poll.status === 'failed') {
            set({ themeSuggestionStatus: 'failed', isSuggestingThemes: false, pendingSuggestions: [], bankMatches: [] })
            return
          }

          // status is null OR completed-but-empty — nothing to render
          set({
            themeSuggestionStatus: null,
            pendingSuggestions: [],
            bankMatches: [],
            isSuggestingThemes: false,
          })
        } catch (err) {
          // A bid with no assessment yet is no longer an error — the endpoint
          // returns 200 with a null status, handled by the branch above.
          // Anything reaching here is a real fault; don't surface it as a
          // user-visible error on mount, just log and move on.
          // eslint-disable-next-line no-console
          console.warn('Failed to load theme suggestion status', err)
        }
      },

      acceptSuggestion: async (bidId: string, sid: string) => {
        const previous = get().pendingSuggestions
        // Optimistic remove from pending; themes side stays server-authoritative.
        set({ pendingSuggestions: previous.filter(p => p.id !== sid) })
        try {
          const created = await apiRequest<WinTheme>(
            `/bids/${bidId}/strategy/themes/suggest`,
            { method: 'POST', body: { _action: 'accept', sid } }
          )
          set(state => ({ themes: [...state.themes, created] }))
          return true
        } catch (err) {
          // The rollback restores the card, which on its own is indistinguishable from a
          // dead button — the caller has to surface the failure.
          set({ pendingSuggestions: previous, error: (err as Error).message })
          return false
        }
      },

      acceptAllSuggestions: async (bidId: string) => {
        const previous = get().pendingSuggestions
        set({ pendingSuggestions: [] })
        try {
          const created = await apiRequest<WinTheme[]>(
            `/bids/${bidId}/strategy/themes/suggest`,
            { method: 'POST', body: { _action: 'accept-all' } }
          )
          set(state => ({ themes: [...state.themes, ...created] }))
          return true
        } catch (err) {
          set({ pendingSuggestions: previous, error: (err as Error).message })
          return false
        }
      },

      dismissSuggestion: async (bidId: string, sid: string) => {
        const previous = get().pendingSuggestions
        set({ pendingSuggestions: previous.filter(p => p.id !== sid) })
        try {
          await apiRequest(
            `/bids/${bidId}/strategy/themes/suggest`,
            { method: 'POST', body: { _action: 'dismiss', sid } }
          )
        } catch (err) {
          set({ pendingSuggestions: previous, error: (err as Error).message })
        }
      },

      discardAllSuggestions: async (bidId: string) => {
        const previousPending = get().pendingSuggestions
        const previousBank = get().bankMatches
        set({
          pendingSuggestions: [],
          bankMatches: [],
          themeSuggestionStatus: null,
        })
        try {
          await apiRequest(
            `/bids/${bidId}/strategy/themes/suggest`,
            { method: 'DELETE' }
          )
        } catch (err) {
          set({ pendingSuggestions: previousPending, bankMatches: previousBank, error: (err as Error).message })
        }
      },

      refinePendingSuggestion: async (bidId: string, sid: string, instruction: string) => {
        try {
          const updated = await apiRequest<PendingThemeSuggestion>(
            `/bids/${bidId}/strategy/themes/suggest`,
            { method: 'POST', body: { _action: 'refine-pending', sid, instruction } }
          )
          set(state => ({
            pendingSuggestions: state.pendingSuggestions.map(p => (p.id === sid ? updated : p)),
          }))
        } catch (err) {
          set({ error: (err as Error).message })
        }
      },

      addBankMatchToBid: async (bidId: string, bankThemeId: string) => {
        const previous = get().bankMatches
        set({ bankMatches: previous.filter(b => b.bankThemeId !== bankThemeId) })
        try {
          const created = await apiRequest<WinTheme>(
            `/bids/${bidId}/strategy/themes/suggest`,
            { method: 'POST', body: { _action: 'add-bank-match', bankThemeId } }
          )
          set(state => ({ themes: [...state.themes, created] }))
        } catch (err) {
          set({ bankMatches: previous, error: (err as Error).message })
        }
      },

      addThemeWithAi: async (bidId: string, userPrompt: string) => {
        // Async: POST fires a self-invoked worker (202), then poll the suggest
        // endpoint until the candidates are ready. Avoids the API Gateway 29s
        // timeout on slow Sonnet generations.
        try {
          await apiRequest(
            `/bids/${bidId}/strategy/themes/suggest`,
            { method: 'POST', body: { _action: 'add-with-ai', userPrompt } }
          )

          for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
            await sleep(POLL_INTERVAL_MS)

            const poll = await apiRequest<{
              addWithAiStatus: 'generating' | 'ready' | 'failed' | null
              addWithAiCandidates: PendingThemeSuggestion[]
            }>(`/bids/${bidId}/strategy/themes/suggest`)

            if (poll.addWithAiStatus === 'ready') {
              return poll.addWithAiCandidates ?? []
            }
            if (poll.addWithAiStatus === 'failed') {
              set({ error: 'AI theme generation failed — please try again' })
              return []
            }
          }

          set({ error: 'AI theme generation timed out — please try again' })
          return []
        } catch (err) {
          set({ error: (err as Error).message })
          return []
        }
      },

      loadCoverageMatrix: async (bidId: string) => {
        try {
          const matrix = await apiRequest<CoverageMatrix>(
            `/bids/${bidId}/strategy/themes/coverage`
          )
          set({ coverageMatrix: matrix })
        } catch (err) {
          set({ error: (err as Error).message })
        }
      },

      refineTheme: async (bidId: string, themeId: string, instruction: string) => {
        // Optimistically mark the theme as refining in local state
        set(state => ({
          themes: state.themes.map(t =>
            t.id === themeId ? { ...t, refineStatus: 'refining' as const, refineResult: null } : t
          ),
        }))
        try {
          // Folded into PATCH /themes/{tid} via the _refine body discriminator —
          // CloudFormation has a 500-resource ceiling on the SAM template, so we
          // can't afford a dedicated endpoint.
          await apiRequest(
            `/bids/${bidId}/strategy/themes/${themeId}`,
            { method: 'PATCH', body: { _refine: { instruction } } }
          )
          await pollThemeRefinement(bidId, themeId)
        } catch (err) {
          set(state => ({
            themes: state.themes.map(t =>
              t.id === themeId ? { ...t, refineStatus: 'failed' as const } : t
            ),
            error: (err as Error).message,
          }))
        }
      },

      acceptRefinement: async (bidId: string, themeId: string) => {
        const theme = get().themes.find(t => t.id === themeId)
        const refined = theme?.refineResult
        if (!refined) return

        set({ isSavingTheme: true })
        try {
          await get().updateTheme(bidId, themeId, {
            title: refined.title,
            description: refined.description,
            category: refined.category as ThemeCategory,
            strength: refined.strength as ThemeStrength,
            evidence: refined.evidence as ThemeEvidence[],
            sources: refined.sources.length > 0 ? refined.sources : null,
          })
          // Clear refine state from the server and local store
          await get().dismissRefinement(bidId, themeId)
          void get().loadCoverageMatrix(bidId)
        } catch (err) {
          set({ isSavingTheme: false, error: (err as Error).message })
        }
      },

      dismissRefinement: async (bidId: string, themeId: string) => {
        set(state => ({
          themes: state.themes.map(t =>
            t.id === themeId ? { ...t, refineStatus: null, refineResult: null } : t
          ),
        }))
        try {
          // Folded into PATCH (see CloudFormation 500-resource note on refineTheme).
          await apiRequest(
            `/bids/${bidId}/strategy/themes/${themeId}`,
            { method: 'PATCH', body: { _refine: { dismiss: true } } }
          )
        } catch {
          // Non-fatal — UI is already cleared
        }
      },

      // ── Bid Brief ─────────────────────────────

      loadBrief: async (bidId: string) => {
        set({ isLoadingBrief: true, error: null, currentBidId: bidId })
        try {
          const brief = await apiRequest<BidBrief | null>(`/bids/${bidId}/strategy/brief`)
          if (hasMovedToAnotherBid(bidId)) return
          set({ bidBrief: brief, isLoadingBrief: false })
        } catch (err) {
          set({ isLoadingBrief: false, error: (err as Error).message })
        }
      },

      loadBriefGenerationStatus: async (bidId: string) => {
        // `isGeneratingBrief` lives only in memory. Leaving the Strategy tab
        // unmounts StrategySection, which calls reset(); a page refresh drops
        // the flag entirely. Either way a generation that is still running
        // becomes invisible and BidBrief falls back to its empty state — the
        // user sees "no brief yet" while the worker is mid-write. The brief row
        // itself can't answer this: it stays empty until generation lands. Ask
        // the generation endpoint instead. Mirrors loadThemeSuggestionStatus.
        set({ isLoadingBrief: true })
        let status: BriefGenerationStatusResponse | null = null
        try {
          status = await apiRequest<BriefGenerationStatusResponse | null>(
            `/bids/${bidId}/strategy/brief/generate`
          )
        } catch {
          // Non-fatal — the brief itself has already loaded. Restoring progress
          // state is best-effort; don't strand the tab on a spinner for it.
        }
        if (hasMovedToAnotherBid(bidId)) return
        set({ isLoadingBrief: false })
        if (!status) return

        if (status.status === 'pending') {
          const startedAt = Date.parse(status.startedAt)
          if (Number.isFinite(startedAt) && Date.now() - startedAt > BRIEF_GENERATION_STALE_MS) {
            set({
              isGeneratingBrief: false,
              briefGenerationStatus: 'failed',
              briefGenerationError: "Generation didn't finish. Try again.",
            })
            return
          }
          // Restore the spinner BEFORE the in-flight guard: the detached poll
          // loop survives the reset that wiped the flag, so bailing first would
          // leave the UI in the empty state it was already wrongly showing.
          set({
            isGeneratingBrief: true,
            briefGenerationStatus: 'pending',
            briefGenerationError: null,
          })
          if (briefPollingInFlight) return
          await get().pollBriefGeneration(bidId)
          return
        }

        if (status.status === 'failed') {
          // Only surface a stored failure while the brief is still empty. With
          // a brief on screen the banner would outlive the run it describes.
          if (get().bidBrief) return
          set({
            isGeneratingBrief: false,
            briefGenerationStatus: 'failed',
            briefGenerationError: status.errorMessage ?? 'Generation failed',
          })
        }
        // 'completed' needs nothing — loadBrief already holds the applied row.
      },

      saveBrief: async (bidId, patch) => {
        set({ isSavingBrief: true, error: null })
        try {
          const brief = await apiRequest<BidBrief>(
            `/bids/${bidId}/strategy/brief`,
            { method: 'PUT', body: patch }
          )
          if (hasMovedToAnotherBid(bidId)) return
          set({ bidBrief: brief, isSavingBrief: false })
        } catch (err) {
          set({ isSavingBrief: false, error: (err as Error).message })
        }
      },

      deleteBrief: async (bidId) => {
        try {
          await apiRequest<void>(`/bids/${bidId}/strategy/brief`, { method: 'DELETE' })
          if (hasMovedToAnotherBid(bidId)) return
          set({ bidBrief: null })
        } catch (err) {
          set({ error: (err as Error).message })
        }
      },

      setBriefStatus: async (bidId, status) => {
        try {
          const brief = await apiRequest<BidBrief>(
            `/bids/${bidId}/strategy/brief/status`,
            { method: 'PATCH', body: { status } }
          )
          set({ bidBrief: brief })
        } catch (err) {
          set({ error: (err as Error).message })
        }
      },

      generateBrief: async (bidId) => {
        set({ isGeneratingBrief: true, briefGenerationStatus: 'pending', briefGenerationError: null, error: null })
        try {
          await apiRequest<{ generationId: string; status: string }>(
            `/bids/${bidId}/strategy/brief/generate`,
            { method: 'POST' }
          )
          // Fire-and-forget poll loop; the worker writes back to the brief row on completion.
          void get().pollBriefGeneration(bidId)
        } catch (err) {
          set({
            isGeneratingBrief: false,
            briefGenerationStatus: 'failed',
            briefGenerationError: (err as Error).message,
          })
        }
      },

      pollBriefGeneration: async (bidId: string) => {
        if (briefPollingInFlight) return
        briefPollingInFlight = true
        try {
          for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
            await sleep(POLL_INTERVAL_MS)
            const status = await apiRequest<BriefGenerationStatusResponse | null>(
              `/bids/${bidId}/strategy/brief/generate`
            )
            // The loop outlives the page — without this it publishes this bid's
            // outcome over whichever bid the user has since opened.
            if (hasMovedToAnotherBid(bidId)) return
            if (!status) continue
            if (status.status === 'completed') {
              // Worker has already auto-applied. Refresh the live brief from the row.
              await get().loadBrief(bidId)
              set({
                isGeneratingBrief: false,
                briefGenerationStatus: 'completed',
                briefGenerationError: null,
              })
              return
            }
            if (status.status === 'failed') {
              set({
                isGeneratingBrief: false,
                briefGenerationStatus: 'failed',
                briefGenerationError: status.errorMessage ?? 'Generation failed',
              })
              return
            }
          }
          // Timed out
          set({
            isGeneratingBrief: false,
            briefGenerationStatus: 'failed',
            briefGenerationError: 'Generation timed out',
          })
        } finally {
          briefPollingInFlight = false
        }
      },

      convertNotesToFields: async (bidId, freeformNotes) => {
        set({ isConvertingNotes: true, convertNotesProposed: null, error: null })
        try {
          const result = await apiRequest<{ structured: BidBriefStructured }>(
            `/bids/${bidId}/strategy/brief/convert-notes`,
            { method: 'POST', body: { freeformNotes } }
          )
          set({ convertNotesProposed: result.structured, isConvertingNotes: false })
        } catch (err) {
          set({ isConvertingNotes: false, error: (err as Error).message })
        }
      },

      applyConvertedNotes: async (bidId, accepted) => {
        // Apply the user-selected subset as a brief upsert. Notes are preserved unchanged
        // (handler honours the absence of freeformNotes in the patch as "leave it alone").
        const existing = get().bidBrief
        const newSource: BidBriefSource = existing?.source === 'ai' ? 'mixed' : 'mixed'
        await get().saveBrief(bidId, {
          structured: accepted,
          source: newSource,
          status: 'draft',
        })
        set({ convertNotesProposed: null })
      },

      clearConvertedNotes: () => set({ convertNotesProposed: null }),

      // ── Utility ───────────────────────────────

      clearError: () => set({ error: null }),
      reset: () => set(initialState),
    }),
    { name: 'strategy-store' }
  )
)

// ===================
// Polling helpers
// ===================

async function pollThemeSuggestions(bidId: string): Promise<void> {
  if (themePollingInFlight) return
  themePollingInFlight = true
  const store = useStrategyStore
  try {
    for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
      await sleep(POLL_INTERVAL_MS)

      // Abort if the user switched bids while polling
      if (hasMovedToAnotherBid(bidId)) return

      const poll = await apiRequest<{
        status: ThemeSuggestionStatus
        suggestions: PendingThemeSuggestionsBlob | null
      }>(`/bids/${bidId}/strategy/themes/suggest`)

      if (poll.status === 'completed' && poll.suggestions) {
        store.setState({
          isSuggestingThemes: false,
          themeSuggestionStatus: 'completed',
          pendingSuggestions: poll.suggestions.aiSuggestions ?? [],
          bankMatches: poll.suggestions.bankMatches ?? [],
        })
        return
      }
      if (poll.status === 'failed') {
        store.setState({
          isSuggestingThemes: false,
          themeSuggestionStatus: 'failed',
          error: 'AI theme suggestion failed — please try again',
        })
        return
      }
    }
    // Timeout
    store.setState({
      isSuggestingThemes: false,
      themeSuggestionStatus: 'failed',
      error: 'AI theme suggestion timed out — please try again',
    })
  } catch (err) {
    store.setState({
      isSuggestingThemes: false,
      themeSuggestionStatus: 'failed',
      error: (err as Error).message,
    })
  } finally {
    themePollingInFlight = false
  }
}

async function pollThemeRefinement(bidId: string, themeId: string): Promise<void> {
  if (refineThemePollsInFlight.has(themeId)) return
  refineThemePollsInFlight.add(themeId)
  const store = useStrategyStore
  try {
    for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
      await sleep(POLL_INTERVAL_MS)

      if (hasMovedToAnotherBid(bidId)) return

      // Poll via the themes list — refineStatus + refineResult are columns on WinTheme.
      // We avoid a dedicated status endpoint because CloudFormation has a 500-resource
      // ceiling on the SAM template.
      const themes = await apiRequest<WinTheme[]>(`/bids/${bidId}/strategy/themes`)
      const theme = themes.find(t => t.id === themeId)
      if (!theme) return

      if (theme.refineStatus === 'completed' && theme.refineResult) {
        store.setState(state => ({
          themes: state.themes.map(t =>
            t.id === themeId
              ? { ...t, refineStatus: 'completed' as const, refineResult: theme.refineResult }
              : t
          ),
        }))
        return
      }
      if (theme.refineStatus === 'failed') {
        store.setState(state => ({
          themes: state.themes.map(t =>
            t.id === themeId ? { ...t, refineStatus: 'failed' as const } : t
          ),
          error: 'AI theme refinement failed — please try again',
        }))
        return
      }
    }
    // Timeout
    store.setState(state => ({
      themes: state.themes.map(t =>
        t.id === themeId ? { ...t, refineStatus: 'failed' as const } : t
      ),
      error: 'AI theme refinement timed out — please try again',
    }))
  } catch (err) {
    store.setState(state => ({
      themes: state.themes.map(t =>
        t.id === themeId ? { ...t, refineStatus: 'failed' as const } : t
      ),
      error: (err as Error).message,
    }))
  } finally {
    refineThemePollsInFlight.delete(themeId)
  }
}

/**
 * Non-terminal poll exits must clear the LOCAL 'scoring' status too, not just the
 * spinner. GoNoGoAssessment derives "a run is in flight" from
 * `isAiScoring || assessment.aiScoringStatus === 'scoring'`, and a resumed run was
 * loaded from a 'scoring' row — so clearing only `isAiScoring` leaves the progress
 * readout ticking its elapsed clock forever while `error` says the run failed.
 */
function clearedScoringStatus(
  assessment: GoNoGoAssessment | null
): GoNoGoAssessment | null {
  if (!assessment || assessment.aiScoringStatus !== 'scoring') return assessment
  return { ...assessment, aiScoringStatus: 'failed' }
}

/**
 * The per-criterion equivalent. A single-criterion run never sets the
 * assessment-level status, so `clearedScoringStatus` would leave the card's own
 * spinner turning (GoNoGoCriterionCard reads `criterion.aiScoringStatus`) beside
 * a notice saying we stopped watching.
 */
function clearedCriterionScoringStatus(
  assessment: GoNoGoAssessment | null,
  criterionId: string
): GoNoGoAssessment | null {
  if (!assessment) return assessment
  return {
    ...assessment,
    criteria: assessment.criteria.map(c =>
      c.id === criterionId && c.aiScoringStatus === 'scoring'
        ? { ...c, aiScoringStatus: 'failed' as const }
        : c
    ),
  }
}

async function pollAiScoring(bidId: string): Promise<void> {
  if (scoringPollingInFlight) return
  scoringPollingInFlight = true
  const store = useStrategyStore
  try {
    for (let i = 0; i < SCORING_MAX_POLL_ATTEMPTS; i++) {
      await sleep(POLL_INTERVAL_MS)

      // Abort if the user switched bids while polling
      if (hasMovedToAnotherBid(bidId)) return

      const refreshed = await apiRequest<GoNoGoAssessment | null>(
        `/bids/${bidId}/strategy/assessment`,
      )
      if (!refreshed) {
        // Assessment disappeared mid-poll (deleted, permissions changed, etc.)
        // Stop polling and clear spinner so the UI isn't stuck.
        store.setState(state => ({
          assessment: clearedScoringStatus(state.assessment),
          isAiScoring: false,
        }))
        return
      }

      // Mid-run refresh: merge ONLY the progress fields so the staged readout
      // advances (stage + elapsed clock). Replacing the whole assessment here
      // would clobber a user's in-flight local criterion edits.
      store.setState(state => state.assessment
        ? {
            assessment: {
              ...state.assessment,
              aiScoringStage: refreshed.aiScoringStage,
              aiScoringStartedAt: refreshed.aiScoringStartedAt,
            },
          }
        : {})

      if (refreshed.aiScoringStatus === 'completed' || refreshed.aiScoringStatus === 'failed') {
        store.setState({
          assessment: refreshed,
          isAiScoring: false,
          aiScoringError: refreshed.aiScoringStatus === 'failed'
            ? { kind: 'failed', message: 'AI scoring failed. Nothing was saved — try again.' }
            : null,
        })
        return
      }
    }

    // Budget exhausted. The run is NOT known to have failed — the server may
    // still be finishing — so this must not offer a re-run.
    // `final` is the row we timed out ON, so it still reads 'scoring'; storing it
    // raw would re-arm the stuck readout the same way the two exits above did.
    const final = await apiRequest<GoNoGoAssessment | null>(`/bids/${bidId}/strategy/assessment`)
    store.setState({
      assessment: clearedScoringStatus(final),
      isAiScoring: false,
      aiScoringError: {
        kind: 'still_running',
        message: 'Still scoring — this is taking longer than usual. Check again in a minute.',
      },
    })
  } catch (err) {
    // A dropped poll request says nothing about the RUN — a 500, a lost
    // connection or a token-refresh blip all leave the worker happily scoring.
    // Classifying it `failed` would offer a Try again that starts a second
    // ~$0.20 run against a live one, so it is `still_running`: refetch only.
    // (Contrast `triggerAiScoring`'s catch, which IS `failed` — there the POST
    // never landed, so no run exists.) `error` keeps its old generic value.
    store.setState(state => ({
      assessment: clearedScoringStatus(state.assessment),
      isAiScoring: false,
      error: (err as Error).message,
      aiScoringError: {
        kind: 'still_running',
        message: 'Lost contact while scoring. Check again in a minute.',
      },
    }))
  } finally {
    scoringPollingInFlight = false
  }
}

const criterionPollsInFlight = new Set<string>()

async function pollCriterionAiScoring(bidId: string, criterionId: string): Promise<void> {
  if (criterionPollsInFlight.has(criterionId)) return
  criterionPollsInFlight.add(criterionId)
  const store = useStrategyStore
  try {
    // Same 300s Lambda ceiling as the batch run — one criterion is cheaper, but
    // it is the same function and the same Sonnet call.
    for (let i = 0; i < SCORING_MAX_POLL_ATTEMPTS; i++) {
      await sleep(POLL_INTERVAL_MS)

      if (hasMovedToAnotherBid(bidId)) return

      const refreshed = await apiRequest<GoNoGoAssessment | null>(
        `/bids/${bidId}/strategy/assessment`,
      )
      if (!refreshed) return

      const target = refreshed.criteria.find(c => c.id === criterionId)
      if (!target) return // criterion was deleted

      if (target.aiScoringStatus === 'completed' || target.aiScoringStatus === 'failed') {
        store.setState({
          assessment: refreshed,
          aiScoringError: target.aiScoringStatus === 'failed'
            ? { kind: 'failed', message: 'AI scoring failed. Nothing was saved — try again.' }
            : null,
        })
        return
      }
    }

    // Budget exhausted — refetch once and say so, without offering a re-run.
    // `clearedScoringStatus` for the same reason the batch loop uses it: `final`
    // is the row we timed out ON, so storing it raw leaves the per-card spinner
    // turning beside a notice that says we stopped watching. The notice is set
    // even when the refetch comes back null, so no exit here is silent.
    const final = await apiRequest<GoNoGoAssessment | null>(`/bids/${bidId}/strategy/assessment`)
    store.setState({
      assessment: clearedCriterionScoringStatus(final, criterionId),
      aiScoringError: {
        kind: 'still_running',
        message: 'Still scoring — this is taking longer than usual. Check again in a minute.',
      },
    })
  } catch (err) {
    // Same reasoning as the batch loop: a dead poll request is not a dead run.
    store.setState(state => ({
      assessment: clearedCriterionScoringStatus(state.assessment, criterionId),
      error: (err as Error).message,
      aiScoringError: {
        kind: 'still_running',
        message: 'Lost contact while scoring. Check again in a minute.',
      },
    }))
  } finally {
    criterionPollsInFlight.delete(criterionId)
  }
}

// ===================
// Test-only helpers
// ===================

/**
 * Reset module-level polling guards. Exposed for tests so they can start
 * from a clean slate — not part of the public API.
 */
export function __resetPollingGuards(): void {
  themePollingInFlight = false
  scoringPollingInFlight = false
  briefPollingInFlight = false
}
